import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import JSZip from 'jszip/dist/jszip.min.js';
import { toast } from 'sonner';
import {
  ArrowLeft, Folder, FolderPlus, Upload, Search, ChevronRight, Home,
  FileText, Image as ImageIcon, Trash2, RotateCcw, Pencil, MoreVertical, X, Loader2, FolderOpen,
  Tag as TagIcon, Eye, Download, Plus, Share2, Copy, Link2, FileArchive,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';
import { LibraryFilePreviewModal } from '../../components/admin/LibraryFilePreviewModal';
import { FileRenameDialog } from '../../components/admin/FileRenameDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const humanBytes = (b) => (b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || ''; } };

const iconForFile = (mime = '') =>
  mime.startsWith('image/') ? ImageIcon : FileText;

// Build a nested tree from the flat folder list returned by the API.
const buildTree = (folders) => {
  const byParent = new Map();
  folders.forEach((f) => {
    const arr = byParent.get(f.parent_id || null) || [];
    arr.push(f);
    byParent.set(f.parent_id || null, arr);
  });
  const attach = (parentId) => (byParent.get(parentId) || []).map((f) => ({
    ...f, children: attach(f.id),
  }));
  return attach(null);
};

// Flatten a tree to [{folder, depth}] with a pre-order DFS traversal.
// Iterative — no inline recursive components (breaks the visual-edits Babel plugin).
const flattenTree = (tree) => {
  const out = [];
  const stack = [...tree.map((f) => ({ f, depth: 0 }))].reverse();
  while (stack.length) {
    const { f, depth } = stack.pop();
    out.push({ f, depth });
    if (f.children?.length) {
      for (let i = f.children.length - 1; i >= 0; i--) {
        stack.push({ f: f.children[i], depth: depth + 1 });
      }
    }
  }
  return out;
};

// Reusable tree renderer (also exported for the composer picker).
export const FolderTree = ({ folders, activeId, onSelect, rootLabel = 'All files', rootCount = 0 }) => {
  const rows = useMemo(() => flattenTree(buildTree(folders)), [folders]);
  return (
    <div className="space-y-0.5" data-testid="folder-tree">
      <button
        type="button"
        onClick={() => onSelect(null)}
        data-testid="folder-node-root"
        className={`w-full text-left flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors ${
          activeId == null ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-gray-100 text-foreground/80'
        }`}
      >
        <Home className="w-4 h-4 flex-shrink-0" />
        <span className="truncate flex-1">{rootLabel}</span>
        {rootCount > 0 && <span className="text-[10px] text-foreground/50 flex-shrink-0">{rootCount}</span>}
      </button>
      {rows.map(({ f, depth }) => {
        const active = activeId === f.id;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onSelect(f.id)}
            data-testid={`folder-node-${f.id}`}
            className={`w-full text-left flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-md transition-colors ${
              active ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-gray-100 text-foreground/80'
            }`}
            style={{ paddingLeft: 8 + depth * 14 }}
          >
            {active ? <FolderOpen className="w-4 h-4 flex-shrink-0" /> : <Folder className="w-4 h-4 flex-shrink-0" />}
            <span className="truncate flex-1">{f.name}</span>
            {f.file_count > 0 && (
              <span className="text-[10px] text-foreground/50 flex-shrink-0">{f.file_count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export const AdminLibrary = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };

  const [folders, setFolders] = useState([]);
  const [rootFileCount, setRootFileCount] = useState(0);
  const [files, setFiles] = useState([]);
  const filesRequestRef = useRef(0);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [search, setSearch] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [renaming, setRenaming] = useState(null); // folder rename
  const [renameFile, setRenameFile] = useState(null);
  const [newFolderModal, setNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [tagEditor, setTagEditor] = useState(null);   // file being tag-edited
  const [preview, setPreview] = useState(null);        // file being previewed
  const [shareFile, setShareFile] = useState(null);    // file being shared
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!password) return;
    loadFiles();
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFolderId, search, showTrash, activeTag]);

  const loadFolders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/library/folders`, { headers: auth });
      setFolders(data.folders || []);
      setRootFileCount(data.root_file_count || 0);
    } catch (e) {
      logger.error('folders load', e);
      if (e.response?.status === 401) navigate('/admin/login');
    }
  };

  const loadTags = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/library/tags`, { headers: auth });
      setTags(data.tags || []);
    } catch (e) { /* non-fatal */ }
  };

  const loadFiles = async () => {
    const requestId = ++filesRequestRef.current;
    try {
      let url = `${API}/admin/library/`;
      if (showTrash) {
        url += 'trash';
      } else {
        const params = new URLSearchParams();
        if (activeTag) { params.set('tag', activeTag); }
        else if (search) { params.set('search', search); params.set('include_all_folders', 'true'); }
        else if (activeFolderId) params.set('folder_id', activeFolderId);
        url += `files?${params.toString()}`;
      }
      const { data } = await axios.get(url, { headers: auth });
      if (requestId === filesRequestRef.current) setFiles(data.files || []);
    } catch (e) { logger.error('files load', e); }
  };

  const activeFolder = folders.find((f) => f.id === activeFolderId);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    // Split out .zip archives so we can offer to unpack them into folders.
    const zips = fileList.filter((f) => /\.zip$/i.test(f.name));
    const rest = fileList.filter((f) => !/\.zip$/i.test(f.name));
    for (const zip of zips) {
      const unpack = window.confirm(
        `"${zip.name}" is a ZIP archive.\n\nUnpack it into folders now (recommended), or upload the ZIP as a single file?`,
      );
      if (unpack) {
        // eslint-disable-next-line no-await-in-loop
        await handleZipImport(zip);
      } else {
        rest.push(zip);
      }
    }
    if (rest.length === 0) return;
    const fd = new FormData();
    if (activeFolderId) fd.append('folder_id', activeFolderId);
    for (const f of rest) fd.append('files', f);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/library/files/upload`, fd, { headers: auth });
      const saved = data.saved?.length || 0;
      const skipped = data.skipped?.length || 0;
      if (saved > 0) toast.success(`Uploaded ${saved} file${saved === 1 ? '' : 's'}`);
      if (skipped > 0) {
        toast.error(`Skipped ${skipped}: ${data.skipped.map((s) => `${s.filename} (${s.reason})`).join(', ')}`);
      }
      await loadFolders();
      await loadFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  // Client-side ZIP extraction — uses JSZip so the server never has to unzip.
  // Each entry inside the archive maps to a real library folder + file
  // under the currently-selected folder, using the same pile + folder
  // creation logic as drag-and-drop folder upload.
  const handleZipImport = async (zipFile) => {
    setUploading(true);
    let toastId = null;
    try {
      toastId = toast.loading(`Unpacking ${zipFile.name}…`);
      const zip = await JSZip.loadAsync(zipFile);
      const folderCache = {};
      const pile = new Map();
      const startFolder = activeFolderId || null;
      const entries = Object.values(zip.files).filter((z) => !z.dir);
      if (entries.length === 0) {
        toast.error('ZIP is empty', { id: toastId });
        return;
      }
      // Strip the enclosing folder if the whole archive is inside one — e.g.
      // "MyBundle/subA/file.pdf" -> "subA/file.pdf" when everything shares
      // "MyBundle/" as the first segment. Mirrors how macOS Finder & Windows
      // Explorer both auto-collapse a single-root archive on extract.
      const topSegments = new Set(entries.map((e) => e.name.split('/')[0]));
      const stripTop = topSegments.size === 1 && entries.every((e) => e.name.includes('/'));
      let processed = 0;
      for (const entry of entries) {
        let relPath = entry.name;
        if (stripTop) relPath = relPath.split('/').slice(1).join('/');
        // Ignore macOS metadata + hidden files
        if (!relPath || relPath.includes('__MACOSX') || /(^|\/)\._/.test(relPath) || relPath.endsWith('/.DS_Store')) continue;
        const segments = relPath.split('/').filter(Boolean);
        const folderSegs = segments.slice(0, -1);
        const fname = segments[segments.length - 1];
        let parentId = startFolder;
        for (const seg of folderSegs) {
          // eslint-disable-next-line no-await-in-loop
          parentId = await findOrCreateFolder(seg, parentId, folderCache);
        }
        // eslint-disable-next-line no-await-in-loop
        const blob = await entry.async('blob');
        const file = new File([blob], fname, { type: blob.type || 'application/octet-stream' });
        const bucket = pile.get(parentId || 'root') || [];
        bucket.push(file);
        pile.set(parentId || 'root', bucket);
        processed += 1;
        if (processed % 5 === 0) toast.loading(`Unpacking… (${processed}/${entries.length})`, { id: toastId });
      }
      await uploadPile(pile);
      toast.success(`Imported ${processed} file${processed === 1 ? '' : 's'} from ${zipFile.name}`, { id: toastId });
      await loadFolders();
      await loadFiles();
    } catch (err) {
      logger.error('zip import', err);
      toast.error(err.message || 'ZIP import failed', { id: toastId });
    } finally { setUploading(false); }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/admin/library/folders`, {
        name: newFolderName.trim(),
        parent_id: activeFolderId,
      }, { headers: auth });
      setNewFolderName('');
      setNewFolderModal(false);
      toast.success('Folder created');
      await loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
  };

  const softDeleteFile = async (f) => {
    if (!window.confirm(`Move '${f.filename}' to trash?`)) return;
    await axios.delete(`${API}/admin/library/files/${f.id}`, { headers: auth });
    toast.success('Moved to trash');
    await loadFiles();
    await loadFolders();
  };

  const restoreFile = async (f) => {
    await axios.post(`${API}/admin/library/files/${f.id}/restore`, {}, { headers: auth });
    toast.success('Restored');
    await loadFiles();
    await loadFolders();
  };

  const permanentDeleteFile = async (f) => {
    if (!window.confirm(`Permanently delete '${f.filename}'? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/library/files/${f.id}/permanent`, { headers: auth });
    toast.success('Deleted permanently');
    await loadFiles();
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder '${f.name}' and everything inside it? Files will go to trash.`)) return;
    await axios.delete(`${API}/admin/library/folders/${f.id}`, { headers: auth });
    toast.success('Folder deleted');
    if (activeFolderId === f.id) setActiveFolderId(null);
    await loadFolders();
    await loadFiles();
  };

  const commitRename = async () => {
    if (!renaming || !renaming.value.trim()) { setRenaming(null); return; }
    try {
      await axios.patch(`${API}/admin/library/folders/${renaming.id}`, { name: renaming.value }, { headers: auth });
      toast.success('Renamed');
      setRenaming(null);
      await loadFolders();
      await loadFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    }
  };

  const moveFile = async (fileId, newFolderId) => {
    try {
      await axios.patch(`${API}/admin/library/files/${fileId}`, { folder_id: newFolderId }, { headers: auth });
      toast.success('Moved');
      await loadFiles();
      await loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
    }
  };

  // -------------------------------------------------------------------
  // Drag & drop — supports plain files AND folder drops (walks the tree
  // via DataTransferItem.webkitGetAsEntry and recreates the folder
  // structure inside the library before uploading each file to the
  // matching folder_id).
  // -------------------------------------------------------------------
  const findOrCreateFolder = async (name, parentId, cache) => {
    const key = `${parentId || 'root'}::${name.toLowerCase()}`;
    if (cache[key]) return cache[key];
    // Try existing (case-insensitive) sibling match
    const existing = folders.find(
      (f) => (f.parent_id || null) === (parentId || null) && f.name.toLowerCase() === name.toLowerCase(),
    );
    if (existing) { cache[key] = existing.id; return existing.id; }
    // Otherwise create — server 409s if there's a race, so treat that as
    // "someone else beat us to it" and refetch.
    try {
      const { data } = await axios.post(`${API}/admin/library/folders`, { name, parent_id: parentId }, { headers: auth });
      cache[key] = data.id;
      folders.push(data);
      return data.id;
    } catch (e) {
      if (e.response?.status === 409) {
        // Refetch and retry
        const { data } = await axios.get(`${API}/admin/library/folders`, { headers: auth });
        setFolders(data.folders || []);
        const hit = (data.folders || []).find(
          (f) => (f.parent_id || null) === (parentId || null) && f.name.toLowerCase() === name.toLowerCase(),
        );
        if (hit) { cache[key] = hit.id; return hit.id; }
      }
      throw e;
    }
  };

  const walkEntry = async (entry, parentFolderId, folderCache, pileByFolder) => {
    if (entry.isFile) {
      const file = await new Promise((res, rej) => entry.file(res, rej));
      const bucket = pileByFolder.get(parentFolderId || 'root') || [];
      bucket.push(file);
      pileByFolder.set(parentFolderId || 'root', bucket);
      return;
    }
    // Directory — create matching folder in our library, then walk children
    const targetFolderId = await findOrCreateFolder(entry.name, parentFolderId, folderCache);
    const reader = entry.createReader();
    const readAll = () => new Promise((res, rej) => {
      const entries = [];
      const step = () => reader.readEntries((chunk) => {
        if (!chunk.length) return res(entries);
        entries.push(...chunk);
        step();
      }, rej);
      step();
    });
    const children = await readAll();
    for (const child of children) {
      await walkEntry(child, targetFolderId, folderCache, pileByFolder);
    }
  };

  const uploadPile = async (pile) => {
    // Sequentially upload each folder's batch to preserve folder_id per file.
    for (const [folderKey, batch] of pile.entries()) {
      const fd = new FormData();
      if (folderKey !== 'root') fd.append('folder_id', folderKey);
      for (const f of batch) fd.append('files', f, f.name);
      try {
        const { data } = await axios.post(`${API}/admin/library/files/upload`, fd, { headers: auth });
        if (data.skipped?.length) {
          toast.error(`Skipped ${data.skipped.length} file(s): ${data.skipped.slice(0, 3).map((s) => s.filename).join(', ')}${data.skipped.length > 3 ? '…' : ''}`);
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed for a batch'); }
    }
  };

  // Folder-picker (input webkitdirectory) uploader. Files carry
  // `webkitRelativePath` like "MyFolder/subA/file.pdf" — we split, mirror
  // each level as a real library folder under the current view, and pile
  // each file into the deepest folder before uploading.
  const handleFolderUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const folderCache = {};
      const pile = new Map();
      const startFolder = activeFolderId || null;
      for (const f of fileList) {
        const rel = f.webkitRelativePath || f.name;
        const segments = rel.split('/').filter(Boolean);
        // Last segment is the filename; anything above are folder segments
        const folderSegments = segments.slice(0, -1);
        let parentId = startFolder;
        for (const seg of folderSegments) {
          // eslint-disable-next-line no-await-in-loop
          parentId = await findOrCreateFolder(seg, parentId, folderCache);
        }
        const bucket = pile.get(parentId || 'root') || [];
        bucket.push(f);
        pile.set(parentId || 'root', bucket);
      }
      await uploadPile(pile);
      toast.success(`Folder uploaded (${fileList.length} file${fileList.length === 1 ? '' : 's'})`);
      await loadFolders();
      await loadFiles();
    } catch (err) {
      logger.error('folder upload', err);
      toast.error(err.response?.data?.detail || 'Folder upload failed');
    } finally { setUploading(false); }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    const items = Array.from(e.dataTransfer?.items || []);
    setUploading(true);
    try {
      if (items.length && items[0].webkitGetAsEntry) {
        // Modern browsers — supports directory drops
        const folderCache = {};
        const pile = new Map();   // folderId (or 'root') → File[]
        for (const item of items) {
          const entry = item.webkitGetAsEntry();
          if (!entry) continue;
          const startFolder = activeFolderId || null;
          if (entry.isDirectory) {
            // Mirror the dropped folder inside the current view
            const newFolderId = await findOrCreateFolder(entry.name, startFolder, folderCache);
            const reader = entry.createReader();
            const readAll = () => new Promise((res, rej) => {
              const entries = [];
              const step = () => reader.readEntries((chunk) => {
                if (!chunk.length) return res(entries);
                entries.push(...chunk);
                step();
              }, rej);
              step();
            });
            const children = await readAll();
            for (const child of children) await walkEntry(child, newFolderId, folderCache, pile);
          } else if (entry.isFile) {
            const file = await new Promise((res, rej) => entry.file(res, rej));
            const bucket = pile.get(startFolder || 'root') || [];
            bucket.push(file);
            pile.set(startFolder || 'root', bucket);
          }
        }
        await uploadPile(pile);
      } else {
        // Fallback — treat as a flat file list
        await handleUpload(Array.from(e.dataTransfer.files || []));
      }
      toast.success('Drop uploaded');
      await loadFolders();
      await loadFiles();
    } catch (err) {
      logger.error('drop upload', err);
      toast.error('Drop failed — see console');
    } finally { setUploading(false); }
  };

  const onDragOver = (e) => { e.preventDefault(); if (!dragActive) setDragActive(true); };
  const onDragLeave = (e) => {
    // Only clear if we've truly left the drop target (not a child bubble).
    if (e.currentTarget === e.target) setDragActive(false);
  };

  const saveFileTags = async (fileId, tagsArr) => {
    try {
      await axios.patch(`${API}/admin/library/files/${fileId}/tags`, { tags: tagsArr }, { headers: auth });
      toast.success('Tags saved');
      setTagEditor(null);
      await loadFiles();
      await loadTags();
    } catch (e) { toast.error('Save tags failed'); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-library">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} data-testid="library-back-dashboard" aria-label="Back to dashboard" className="p-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 shrink-0 bg-primary/10 rounded-lg flex items-center justify-center">
              <Folder className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h1 data-testid="library-heading" className="text-base sm:text-xl font-bold text-primary">File Library</h1>
              <p className="text-xs text-foreground/60">
                Bulk-upload · sub-folders · one-tap attach in the email composer
              </p>
            </div>
          </div>
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <Button
              variant={showTrash ? 'default' : 'outline'}
              onClick={() => { setShowTrash(!showTrash); setSearch(''); }}
              data-testid="toggle-trash"
              size="sm"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Trash
            </Button>
            {!showTrash && (
              <>
                <Button variant="outline" onClick={() => setNewFolderModal(true)} data-testid="new-folder-btn" size="sm">
                  <FolderPlus className="w-4 h-4 mr-1.5" /> New folder
                </Button>
                <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary text-white rounded-md cursor-pointer hover:bg-primary/90 disabled:opacity-60" data-testid="upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload files</>}
                  <input
                    type="file"
                    data-testid="library-file-upload-input"
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => { handleUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
                  />
                </label>
                <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-primary text-primary rounded-md cursor-pointer hover:bg-primary/5 disabled:opacity-60" data-testid="upload-folder-btn" title="Upload a whole folder — sub-folders are recreated inside the library">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><FolderPlus className="w-4 h-4" /> Upload folder</>}
                  <input
                    type="file"
                    data-testid="library-folder-upload-input"
                    /* eslint-disable-next-line react/no-unknown-property */
                    webkitdirectory=""
                    directory=""
                    mozdirectory=""
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => { handleFolderUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Folder tree + tags sidebar */}
        {!showTrash && (
          <aside className="min-w-0 lg:col-span-1 space-y-4">
            <div className="bg-white border border-border rounded-xl p-3 h-fit">
              <FolderTree
                folders={folders}
                activeId={activeFolderId}
                onSelect={(id) => { setActiveFolderId(id); setActiveTag(null); }}
                rootCount={rootFileCount}
              />
              {activeFolder && (
                <button
                  onClick={() => deleteFolder(activeFolder)}
                  data-testid="delete-current-folder"
                  className="mt-3 w-full text-xs text-red-600 hover:bg-red-50 px-2 py-1.5 rounded flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete '{activeFolder.name}'
                </button>
              )}
            </div>
            {tags.length > 0 && (
              <div className="bg-white border border-border rounded-xl p-3" data-testid="tag-chip-list">
                <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-foreground/60 uppercase tracking-wide">
                  <TagIcon className="w-3 h-3" /> Tags
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => { setActiveTag(activeTag === t.name ? null : t.name); setActiveFolderId(null); }}
                      data-testid={`tag-chip-${t.name}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border transition-colors ${
                        activeTag === t.name
                          ? 'bg-primary text-white border-primary'
                          : 'bg-primary/5 text-primary border-primary/20 hover:bg-primary/10'
                      }`}
                    >
                      {t.name}
                      <span className="text-[10px] opacity-70">{t.count}</span>
                    </button>
                  ))}
                  {activeTag && (
                    <button
                      onClick={() => setActiveTag(null)}
                      className="text-[11px] text-foreground/50 hover:text-foreground underline"
                    >
                      clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </aside>
        )}

        {/* Files main — with drop zone */}
        <main
          className={`min-w-0 ${showTrash ? 'lg:col-span-4' : 'lg:col-span-3'}`}
          onDrop={showTrash ? undefined : onDrop}
          onDragOver={showTrash ? undefined : onDragOver}
          onDragLeave={showTrash ? undefined : onDragLeave}
          data-testid="library-drop-zone"
        >
          {dragActive && !showTrash && (
            <div className="fixed inset-0 bg-primary/10 border-4 border-dashed border-primary z-40 pointer-events-none flex items-center justify-center" data-testid="drop-overlay">
              <div className="bg-white rounded-xl px-6 py-4 shadow-lg text-center">
                <Upload className="w-8 h-8 text-primary mx-auto mb-2" />
                <div className="text-base font-semibold text-primary">Drop to upload</div>
                <div className="text-xs text-foreground/60">Folders will keep their structure</div>
              </div>
            </div>
          )}
          {/* Breadcrumb + search */}
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            {showTrash ? (
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Trash · auto-purged after 30 days
              </h2>
            ) : (
              <div className="flex min-w-0 flex-wrap items-center gap-1 break-words text-sm text-foreground/70" data-testid="breadcrumb">
                <Home className="w-4 h-4" />
                <span>Library</span>
                {activeFolder?.path?.split('/').filter(Boolean).map((seg) => (
                  <React.Fragment key={seg}>
                    <ChevronRight className="w-3.5 h-3.5 text-foreground/40" />
                    <span>{seg}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
            {!showTrash && (
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search all files…"
                  className="pl-9"
                  data-testid="library-search"
                />
              </div>
            )}
          </div>

          {!showTrash && selectedIds.size > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 bg-primary/10 border border-primary/30 rounded-lg px-4 py-2.5" data-testid="bulk-share-bar">
              <div className="text-sm">
                <strong>{selectedIds.size}</strong> file{selectedIds.size === 1 ? '' : 's'} selected
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="ml-3 text-xs text-foreground/60 hover:text-foreground underline"
                  data-testid="bulk-clear"
                >Clear</button>
              </div>
              <Button size="sm" onClick={() => setBundleModalOpen(true)} data-testid="bulk-share-btn">
                <Share2 className="w-4 h-4 mr-1.5" /> Bundle &amp; share
              </Button>
            </div>
          )}

          {files.length === 0 ? (
            <div className="bg-white border border-border rounded-xl p-12 text-center">
              <Folder className="w-14 h-14 text-foreground/30 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">
                {showTrash ? 'Trash is empty' : (search ? 'No files match' : 'No files here yet')}
              </h3>
              {!showTrash && !search && (
                <p className="text-sm text-foreground/60">Drag files onto the Upload button to add some.</p>
              )}
            </div>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="block w-full text-sm lg:table lg:table-fixed" data-testid="library-file-list">
                <thead className="block bg-gray-50 border-b border-border lg:table-header-group">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={files.length > 0 && files.every((f) => selectedIds.has(f.id))}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) files.forEach((f) => next.add(f.id));
                          else files.forEach((f) => next.delete(f.id));
                          setSelectedIds(next);
                        }}
                        data-testid="select-all-checkbox"
                        aria-label="Select all files"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-foreground/60 uppercase">Name</th>
                    <th className="hidden w-24 px-4 py-2 text-left text-xs font-semibold text-foreground/60 uppercase lg:table-cell">Size</th>
                    <th className="hidden w-28 px-4 py-2 text-left text-xs font-semibold text-foreground/60 uppercase lg:table-cell">Uploaded</th>
                    <th className="hidden w-72 px-4 py-2 text-right text-xs font-semibold text-foreground/60 uppercase lg:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="block divide-y divide-border lg:table-row-group">
                  {files.map((f) => {
                    const Icon = iconForFile(f.mime_type);
                    return (
                      <tr key={f.id} data-testid={`file-row-${f.id}`} className={`grid grid-cols-[2rem_minmax(0,1fr)] gap-x-1 p-2 hover:bg-gray-50/60 lg:table-row lg:p-0 ${selectedIds.has(f.id) ? 'bg-primary/5' : ''}`}>
                        <td className="w-8 px-2 py-3 lg:px-3 lg:py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(f.id)}
                            onChange={(e) => {
                              const next = new Set(selectedIds);
                              if (e.target.checked) next.add(f.id); else next.delete(f.id);
                              setSelectedIds(next);
                            }}
                            data-testid={`file-select-${f.id}`}
                            aria-label={`Select ${f.filename}`}
                          />
                        </td>
                        <td className="min-w-0 px-2 py-2 lg:px-4">
                          <div className="flex min-w-0 items-center gap-2">
                            <Icon className="w-4 h-4 text-foreground/50 flex-shrink-0" />
                            <button
                                type="button"
                                onClick={() => setPreview(f)}
                                className="min-h-11 min-w-0 flex-1 break-words text-left text-teal-800 hover:underline font-medium lg:min-h-0 lg:truncate"
                                title={f.filename}
                                data-testid={`file-open-${f.id}`}
                              >
                                {f.filename}
                            </button>
                          </div>
                          {!showTrash && <button type="button" onClick={() => setRenameFile(f)} data-testid={`file-rename-${f.id}`}
                            className="ml-6 mt-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs text-primary transition-colors hover:bg-primary/10" aria-label={`Rename ${f.filename}`}>
                            <Pencil className="h-3 w-3" />Rename
                          </button>}
                          {(f.tags?.length > 0) && (
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {f.tags.map((t, index) => (
                                <span key={t} data-testid={`file-tag-${f.id}-${index}`} className="inline-flex max-w-full items-center gap-1 break-words px-1.5 py-0.5 text-[10px] rounded-full bg-primary/5 border border-primary/20 text-primary">
                                  <TagIcon className="w-2.5 h-2.5" /> {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td data-testid={`file-size-${f.id}`} className="col-start-2 px-2 pb-1 text-xs text-foreground/70 lg:px-4 lg:py-2 lg:text-sm">{humanBytes(f.size_bytes || 0)}</td>
                        <td data-testid={`file-uploaded-${f.id}`} className="col-start-2 px-2 pb-1 text-xs text-foreground/60 lg:px-4 lg:py-2">{fmtDate(f.uploaded_at)}</td>
                        <td className="col-span-2 min-w-0 px-2 py-2 lg:px-4">
                          <div className="flex min-w-0 flex-wrap items-center justify-start gap-1 lg:justify-end" data-testid={`file-actions-${f.id}`}>
                            {showTrash ? (
                              <>
                                <button onClick={() => restoreFile(f)} data-testid={`file-restore-${f.id}`} title="Restore" className="min-h-11 min-w-11 p-2 rounded hover:bg-emerald-50 text-emerald-600 lg:min-h-0 lg:min-w-0">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => permanentDeleteFile(f)} data-testid={`file-purge-${f.id}`} title="Delete permanently" className="min-h-11 min-w-11 p-2 rounded hover:bg-red-50 text-red-600 lg:min-h-0 lg:min-w-0">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setPreview(f)}
                                  data-testid={`file-preview-${f.id}`}
                                  title="Preview"
                                  className="flex min-h-11 min-w-11 items-center justify-center p-2 rounded hover:bg-gray-100 text-foreground/60 lg:min-h-0 lg:min-w-0"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setTagEditor({ id: f.id, filename: f.filename, tags: [...(f.tags || [])] })}
                                  data-testid={`file-tags-${f.id}`}
                                  title="Edit tags"
                                  className="flex min-h-11 min-w-11 items-center justify-center p-2 rounded hover:bg-gray-100 text-foreground/60 lg:min-h-0 lg:min-w-0"
                                >
                                  <TagIcon className="w-3.5 h-3.5" />
                                </button>
                                <select
                                  value={f.folder_id || ''}
                                  onChange={(e) => moveFile(f.id, e.target.value || null)}
                                  data-testid={`file-move-${f.id}`}
                                  className="order-last min-h-11 w-full min-w-0 max-w-full rounded border border-border px-2 py-2 text-xs sm:w-auto sm:max-w-xs lg:order-none lg:min-h-0 lg:w-28 lg:py-1"
                                  title="Move to folder"
                                >
                                  <option value="">— Root —</option>
                                  {folders.map((fld) => (
                                    <option key={fld.id} value={fld.id}>{fld.path}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => setShareFile(f)}
                                  data-testid={`file-share-${f.id}`}
                                  title={f.share_token ? 'Share link (active)' : 'Create share link'}
                                  className={`flex min-h-11 min-w-11 items-center justify-center p-2 rounded hover:bg-primary/10 lg:min-h-0 lg:min-w-0 ${f.share_token ? 'text-primary' : 'text-foreground/60'}`}
                                >
                                  <Share2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => softDeleteFile(f)}
                                  data-testid={`file-delete-${f.id}`}
                                  title="Move to trash"
                                  className="flex min-h-11 min-w-11 items-center justify-center p-2 rounded hover:bg-red-50 text-red-600 lg:min-h-0 lg:min-w-0"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {/* File preview modal — inline PDF, images, plain text; graceful
          fallback for other formats (download button). */}
      {renameFile && <FileRenameDialog key={renameFile.id} file={renameFile} onClose={() => setRenameFile(null)} onRenamed={(updated) => {
        setFiles((previous) => previous.map((file) => file.id === updated.id ? updated : file));
        setPreview((previous) => previous?.id === updated.id ? updated : previous);
        setShareFile((previous) => previous?.id === updated.id ? updated : previous);
        setRenameFile(null);
        if (search) setSearch(updated.filename);
        toast.success('File renamed');
      }} />}
      {preview && (
        <LibraryFilePreviewModal key={preview.id} file={preview} onClose={() => setPreview(null)} />
      )}

      {shareFile && (
        <ShareLinkModal
          file={shareFile}
          onClose={() => setShareFile(null)}
          onChange={(updated) => {
            setFiles((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setShareFile(updated);
          }}
        />
      )}

      {bundleModalOpen && (
        <BundleShareModal
          fileIds={Array.from(selectedIds)}
          files={files.filter((f) => selectedIds.has(f.id))}
          onClose={() => setBundleModalOpen(false)}
          onCreated={() => {
            setBundleModalOpen(false);
            setSelectedIds(new Set());
          }}
        />
      )}

      {/* Tag editor modal */}
      {tagEditor && (
        <TagEditorModal
          file={tagEditor}
          existingTags={tags}
          onCancel={() => setTagEditor(null)}
          onSave={(newTags) => saveFileTags(tagEditor.id, newTags)}
        />
      )}

      {/* New folder modal */}
      {newFolderModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="new-folder-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold">Create folder</h3>
              <button onClick={() => setNewFolderModal(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label>Folder name</Label>
                <Input
                  autoFocus
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
                  placeholder="Marketing"
                  data-testid="new-folder-input"
                />
              </div>
              <p className="text-xs text-foreground/50">
                {activeFolder ? `Creates inside "${activeFolder.path}"` : 'Creates at the root of your library'}
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewFolderModal(false)}>Cancel</Button>
              <Button onClick={createFolder} data-testid="new-folder-save">Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLibrary;

// ---------------------------------------------------------------------------
// Tag editor modal — add / remove tags on a single file. Suggests any
// already-in-use tags so the vocabulary stays consistent.
// ---------------------------------------------------------------------------
const TagEditorModal = ({ file, existingTags, onCancel, onSave }) => {
  const [current, setCurrent] = useState([...(file.tags || [])]);
  const [draft, setDraft] = useState('');
  const suggestions = (existingTags || [])
    .map((t) => t.name)
    .filter((n) => !current.some((c) => c.toLowerCase() === n.toLowerCase()));

  const addTag = (name) => {
    const n = (name || '').trim();
    if (!n) return;
    if (current.some((c) => c.toLowerCase() === n.toLowerCase())) return;
    if (current.length >= 15) return;
    setCurrent([...current, n]);
    setDraft('');
  };
  const removeTag = (name) => setCurrent(current.filter((c) => c !== name));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="tag-editor-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="font-semibold">Tags</h3>
            <div className="text-xs text-foreground/60 truncate" title={file.filename}>{file.filename}</div>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex flex-wrap gap-1.5 min-h-[32px] border border-border rounded-md px-2 py-2 bg-gray-50/50">
            {current.length === 0 && <span className="text-xs text-foreground/40">No tags yet</span>}
            {current.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                {t}
                <button onClick={() => removeTag(t)} className="hover:text-red-600" data-testid={`tag-remove-${t}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(draft); } }}
              placeholder="Add a tag and press Enter"
              className="flex-1"
              data-testid="tag-editor-input"
              maxLength={40}
            />
            <Button onClick={() => addTag(draft)} data-testid="tag-editor-add"><Plus className="w-4 h-4" /></Button>
          </div>
          {suggestions.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-foreground/50 mb-1.5">Existing tags</div>
              <div className="flex flex-wrap gap-1">
                {suggestions.slice(0, 12).map((s) => (
                  <button
                    key={s}
                    onClick={() => addTag(s)}
                    data-testid={`tag-suggest-${s}`}
                    className="px-2 py-0.5 text-xs rounded-full bg-gray-100 hover:bg-primary/10 text-foreground/70 hover:text-primary border border-border"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(current)} data-testid="tag-editor-save">Save tags</Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Share-link modal — mint or revoke a signed public URL for one-click
// WhatsApp / SMS sharing. No client sign-in required to open the link.
// ---------------------------------------------------------------------------
const EXPIRY_PRESETS = [
  { label: '7 days',   days: 7 },
  { label: '30 days',  days: 30 },
  { label: '90 days',  days: 90 },
  { label: 'Never expires', days: 0 },
];

const ShareLinkModal = ({ file, onClose, onChange }) => {
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };
  const [expiryDays, setExpiryDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const publicBase = process.env.REACT_APP_BACKEND_URL;
  const shareUrl = file.share_token ? `${publicBase}/api/public/library/share/${file.share_token}` : null;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/library/files/${file.id}/share`,
        { expires_in_days: expiryDays },
        { headers: auth },
      );
      onChange({
        ...file,
        share_token: data.token,
        share_expires_at: data.expires_at,
        share_created_at: data.created_at,
      });
      toast.success(file.share_token ? 'Share link rotated' : 'Share link created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create share link'); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this share link? Anyone who has it will no longer be able to open the file.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/library/files/${file.id}/share`, { headers: auth });
      onChange({ ...file, share_token: null, share_expires_at: null, share_created_at: null });
      toast.success('Share link revoked');
    } catch (e) { toast.error('Revoke failed'); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    } catch { toast.error('Copy failed — long-press the link to copy'); }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(`Here's the file we discussed: ${shareUrl}`)}`;
  const sms = `sms:?&body=${encodeURIComponent(`Here's the file we discussed: ${shareUrl}`)}`;
  const mail = `mailto:?subject=${encodeURIComponent(file.filename)}&body=${encodeURIComponent(`Here's the file we discussed:\n\n${shareUrl}`)}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-foreground/60 font-semibold">Share link</div>
            <div className="font-semibold text-sm truncate" title={file.filename}>{file.filename}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" data-testid="share-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!file.share_token ? (
            <>
              <p className="text-sm text-foreground/70">
                Create a signed link so a client can open this file straight from
                WhatsApp or SMS — no admin sign-in required.
              </p>
              <div>
                <Label>Expires in</Label>
                <div className="grid grid-cols-4 gap-1.5 mt-1">
                  {EXPIRY_PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p.days}
                      onClick={() => setExpiryDays(p.days)}
                      className={`px-2 py-1.5 text-xs rounded border ${expiryDays === p.days ? 'bg-primary text-white border-primary' : 'border-border hover:bg-gray-50'}`}
                      data-testid={`share-expiry-${p.days}`}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
              <Button onClick={create} disabled={busy} className="w-full" data-testid="share-create">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : <><Link2 className="w-4 h-4 mr-1.5" /> Create share link</>}
              </Button>
            </>
          ) : (
            <>
              <div>
                <Label>Public link</Label>
                <div className="mt-1 flex items-center gap-1.5">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 px-2 py-1.5 text-xs font-mono border border-border rounded bg-gray-50 truncate"
                    data-testid="share-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <button onClick={copy} className="p-2 border border-border rounded hover:bg-gray-50" data-testid="share-copy" title="Copy link"><Copy className="w-4 h-4" /></button>
                </div>
                <p className="text-[11px] text-foreground/50 mt-1">
                  {file.share_expires_at
                    ? `Expires ${new Date(file.share_expires_at).toLocaleString()}`
                    : 'Never expires'}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                <a href={wa} target="_blank" rel="noopener noreferrer" className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-emerald-50 hover:border-emerald-300" data-testid="share-whatsapp">WhatsApp</a>
                <a href={sms} className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-blue-50 hover:border-blue-300" data-testid="share-sms">SMS</a>
                <a href={mail} className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-gray-50" data-testid="share-email">Email</a>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button variant="outline" size="sm" onClick={create} disabled={busy} data-testid="share-rotate">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rotate link'}
                </Button>
                <Button variant="outline" size="sm" onClick={revoke} disabled={busy} className="text-red-600 hover:bg-red-50" data-testid="share-revoke">
                  <Trash2 className="w-4 h-4 mr-1" /> Revoke
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Bundle-share modal — packs the selected files into a single ZIP and hands
// back one public URL. Perfect for full pet-relocation packets.
// ---------------------------------------------------------------------------
const BundleShareModal = ({ fileIds, files, onClose, onCreated }) => {
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };
  const publicBase = process.env.REACT_APP_BACKEND_URL;

  const defaultName = files.length === 1
    ? files[0].filename.replace(/\.[^.]+$/, '')
    : `Pawsome bundle · ${files.length} files`;
  const [name, setName] = useState(defaultName);
  const [expiryDays, setExpiryDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);   // { share_token, expires_at }

  const totalBytes = files.reduce((s, f) => s + (f.size_bytes || 0), 0);
  const humanTotal = totalBytes < 1024 * 1024
    ? `${(totalBytes / 1024).toFixed(1)} KB`
    : `${(totalBytes / 1024 / 1024).toFixed(1)} MB`;

  const shareUrl = created ? `${publicBase}/api/public/library/bundle/${created.share_token}` : null;

  const create = async () => {
    if (fileIds.length === 0) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/library/bundles`,
        { name: name.trim(), file_ids: fileIds, expires_in_days: expiryDays },
        { headers: auth },
      );
      setCreated({ share_token: data.share_token, expires_at: data.expires_at });
      toast.success(`Bundle created (${fileIds.length} files)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bundle create failed');
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); }
    catch { toast.error('Copy failed — long-press the link'); }
  };

  const wa   = created && `https://wa.me/?text=${encodeURIComponent(`Here's your Pawsome relocation packet — one link, all the paperwork: ${shareUrl}`)}`;
  const sms  = created && `sms:?&body=${encodeURIComponent(`Here's your Pawsome relocation packet: ${shareUrl}`)}`;
  const mail = created && `mailto:?subject=${encodeURIComponent(name)}&body=${encodeURIComponent(`Here's your Pawsome relocation packet — one download, all the paperwork:\n\n${shareUrl}`)}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bundle-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-foreground/60 font-semibold">Bundle &amp; share</div>
            <div className="font-semibold text-sm">{fileIds.length} file{fileIds.length === 1 ? '' : 's'} · {humanTotal}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" data-testid="bundle-close"><X className="w-5 h-5" /></button>
        </div>

        {!created ? (
          <>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <Label>Bundle name (shown in the ZIP filename)</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rocky · Cayman to UK packet" data-testid="bundle-name" />
              </div>
              <div>
                <Label>Expires in</Label>
                <div className="grid grid-cols-4 gap-1.5 mt-1">
                  {EXPIRY_PRESETS.map((p) => (
                    <button
                      type="button"
                      key={p.days}
                      onClick={() => setExpiryDays(p.days)}
                      className={`px-2 py-1.5 text-xs rounded border ${expiryDays === p.days ? 'bg-primary text-white border-primary' : 'border-border hover:bg-gray-50'}`}
                      data-testid={`bundle-expiry-${p.days}`}
                    >{p.label}</button>
                  ))}
                </div>
              </div>
              <div className="border border-border rounded-lg divide-y divide-border max-h-40 overflow-y-auto">
                {files.map((f) => (
                  <div key={f.id} className="px-3 py-1.5 text-xs flex items-center justify-between gap-2">
                    <span className="truncate" title={f.filename}>{f.filename}</span>
                    <span className="text-foreground/50 shrink-0">{f.size_bytes ? `${(f.size_bytes / 1024).toFixed(1)} KB` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={create} disabled={busy || !name.trim()} data-testid="bundle-create">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : <><Link2 className="w-4 h-4 mr-1.5" /> Create link</>}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-5 space-y-4">
              <div>
                <Label>Public bundle link</Label>
                <div className="mt-1 flex items-center gap-1.5">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 px-2 py-1.5 text-xs font-mono border border-border rounded bg-gray-50 truncate"
                    data-testid="bundle-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <button onClick={copy} className="p-2 border border-border rounded hover:bg-gray-50" data-testid="bundle-copy" title="Copy link"><Copy className="w-4 h-4" /></button>
                </div>
                <p className="text-[11px] text-foreground/50 mt-1">
                  {created.expires_at
                    ? `Expires ${new Date(created.expires_at).toLocaleString()}`
                    : 'Never expires'}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <a href={wa} target="_blank" rel="noopener noreferrer" className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-emerald-50 hover:border-emerald-300" data-testid="bundle-whatsapp">WhatsApp</a>
                <a href={sms} className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-blue-50 hover:border-blue-300" data-testid="bundle-sms">SMS</a>
                <a href={mail} className="text-center px-2 py-1.5 text-xs border border-border rounded hover:bg-gray-50" data-testid="bundle-email">Email</a>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Button onClick={onCreated} data-testid="bundle-done">Done</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

